import React from 'react';

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientConfig,
  QueryClientProvider,
} from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useLocation } from 'react-router-dom';

import {
  buildAuthReturnState,
  getLocationReturnPath,
} from 'auth/auth-return-path';
import { handleRequestError } from 'helpers/handle-request-error';
import { GENERIC_ERROR_MESSAGE } from 'localization';
import { RequestError } from 'types';

import {
  shouldHandleGlobalMutationReauthentication,
  shouldHandleGlobalQueryError,
  shouldHandleGlobalQueryReauthentication,
} from './query-error-handling';

type ReauthenticationCallback = () => Promise<void> | void;
type NavigationActiveCheck = () => boolean;
type QueryErrorContext = {
  meta?: Record<string, unknown>;
};
type MutationErrorContext = {
  meta?: Record<string, unknown>;
};

const isRequestError = (error: unknown): error is RequestError => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return false;
  }

  const response = (error as { response?: unknown }).response;

  if (!response || typeof response !== 'object' || !('status' in response)) {
    return false;
  }

  return typeof (response as { status?: unknown }).status === 'number';
};

const toRequestError = (error: unknown): RequestError =>
  isRequestError(error) ? error : {};

let reauthenticationCallback: ReauthenticationCallback | undefined;
let isNavigationActive: NavigationActiveCheck = () => false;
let redirectInFlight = false;

const resetReauthenticationGuard = () => {
  redirectInFlight = false;
};

const registerReauthenticationCallback = (
  callback: ReauthenticationCallback,
  activeCheck: NavigationActiveCheck
) => {
  reauthenticationCallback = callback;
  isNavigationActive = activeCheck;
  resetReauthenticationGuard();

  return () => {
    if (reauthenticationCallback === callback) {
      reauthenticationCallback = undefined;
      isNavigationActive = () => false;
      resetReauthenticationGuard();
    }
  };
};

const requestReauthentication = () => {
  const callback = reauthenticationCallback;

  if (redirectInFlight || isNavigationActive() || !callback) {
    return;
  }

  redirectInFlight = true;

  void (async () => {
    try {
      await callback();
    } catch {
      // Keep global error handling non-throwing; AuthProvider owns redirect errors.
    } finally {
      resetReauthenticationGuard();
    }
  })();
};

const handleError = (error: unknown) => {
  if (isRequestError(error) && error.response?.status === 401) {
    requestReauthentication();
  } else handleRequestError(toRequestError(error), GENERIC_ERROR_MESSAGE);
};

const handleGlobalMutationError = (
  error: unknown,
  _variables: unknown,
  _context: unknown,
  mutation: MutationErrorContext
) => {
  if (
    isRequestError(error) &&
    error.response?.status === 401 &&
    shouldHandleGlobalMutationReauthentication(mutation.meta)
  ) {
    requestReauthentication();
  }
};

export const handleGlobalQueryError = (
  error: unknown,
  query: QueryErrorContext
) => {
  if (isRequestError(error) && error.response?.status === 401) {
    if (shouldHandleGlobalQueryReauthentication(query.meta)) {
      requestReauthentication();
    }

    return;
  }

  if (!shouldHandleGlobalQueryError(query.meta)) {
    return;
  }

  handleRequestError(toRequestError(error), GENERIC_ERROR_MESSAGE);
};

export const defaultOptions = {
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      networkMode: 'always',
    },
    mutations: {
      onError(error: unknown) {
        handleError(error);
      },
    },
  },
};

const queryClient = new QueryClient({
  ...defaultOptions,
  mutationCache: new MutationCache({
    onError: handleGlobalMutationError,
  }),
  queryCache: new QueryCache({
    onError: handleGlobalQueryError,
  }),
} as QueryClientConfig);

interface NetworkContextProps {
  children: React.ReactNode;
}

export const NetworkContext: React.FC<NetworkContextProps> = ({ children }) => {
  const { activeNavigator, isAuthenticated, signinRedirect, user } = useAuth();
  const location = useLocation();

  const currentSubject = isAuthenticated ? user?.profile?.sub : undefined;
  const currentReturnPath = getLocationReturnPath(location);

  const previousSubjectRef = React.useRef<string | undefined>();

  // Keep the latest reauth inputs in a ref so the global 401 redirect always
  // uses the current route/navigator without re-subscribing on every
  // navigation (which would also reset the in-flight guard mid-redirect).
  const reauthInputsRef = React.useRef({
    activeNavigator,
    currentReturnPath,
    signinRedirect,
  });

  React.useEffect(() => {
    reauthInputsRef.current = {
      activeNavigator,
      currentReturnPath,
      signinRedirect,
    };
  });

  // Global query/mutation 401s share one redirect guard. /api/admin/me and
  // /api/client/me opt out and own their route-level 401/403 UX because they
  // gate their workspaces. Registered once; the subject-change effect below
  // resets the guard after a successful re-auth.
  React.useEffect(
    () =>
      registerReauthenticationCallback(
        () =>
          reauthInputsRef.current.signinRedirect({
            state: buildAuthReturnState(
              reauthInputsRef.current.currentReturnPath
            ),
          }),
        () => Boolean(reauthInputsRef.current.activeNavigator)
      ),
    []
  );

  React.useEffect(() => {
    const previousSubject = previousSubjectRef.current;

    if (currentSubject) {
      if (previousSubject && previousSubject !== currentSubject) {
        queryClient.clear();
      }

      previousSubjectRef.current = currentSubject;
      resetReauthenticationGuard();

      return;
    }

    if (previousSubject) {
      queryClient.clear();
      previousSubjectRef.current = undefined;
      resetReauthenticationGuard();
    }
  }, [currentSubject]);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
